import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Icon from '../../ui/Icon'
import Loading from '../../loading/Loading'
import { Button } from '../../buttons/Button'
import { CourtSvg } from '../../ui/courtSvg/CourtSvg'
import { GuestCourtCard } from './guestCourtCard/GuestCourtCard'

import { ClubModelMetadata, CourtModel, CourtType } from '../../../types/court/court'
import { formatAddress } from '../../../utils/address/address'
import { displayHourMinute } from '../../../utils/dateUtils'
import { formatMinutes } from '../../../utils/booking/booking'

type GuestClubSectionProps = {
  club: ClubModelMetadata
  courts: CourtModel[]
  courtsLoading?: boolean
}

const toMinutes = (time?: string) => {
  if (!time) {
    return undefined
  }
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/** Whether the club is open right now (browser clock; a close time of 00:00 means midnight). */
const openNow = (openTime?: string, closeTime?: string, now = new Date()) => {
  const open = toMinutes(openTime)
  const closeRaw = toMinutes(closeTime)
  if (open === undefined || closeRaw === undefined) {
    return undefined
  }
  const close = closeRaw <= open ? closeRaw + 24 * 60 : closeRaw
  const current = now.getHours() * 60 + now.getMinutes()
  return (current >= open && current < close) || current + 24 * 60 < close
}

type Perk = { key: string; icon: string; title: string; text: string }

/**
 * Public page of one club: a hero with the live opening status and the key numbers, the club's booking perks, its
 * courts, where to find it and a final call to book.
 */
const GuestClubSection = ({ club, courts, courtsLoading = false }: GuestClubSectionProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation(['clubs', 'courts', 'address'])
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000)
    return () => window.clearInterval(timer)
  }, [])

  const formattedAddress = formatAddress(club.address, {
    number: t('address:number'),
    floor: t('address:floor'),
    apartment: t('address:apartment'),
  })
  const mapsQuery = encodeURIComponent(formattedAddress)
  const mapEmbedSrc = `https://maps.google.com/maps?q=${mapsQuery}&z=16&output=embed`
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`

  const openTime = displayHourMinute(club.openTime)
  const closeTime = displayHourMinute(club.closeTime)
  const isOpen = openNow(club.openTime, club.closeTime, now)
  const courtsCount = courts.length || club.courtsCount || 0
  const indoorCount = courts.filter(court => court.courtType === CourtType.INDOOR).length
  const outdoorCount = courts.length - indoorCount
  const litCount = courts.filter(court => court.lightSwitchName).length
  const durations = [...(club.sessionDurations ?? [])].sort((a, b) => a - b)
  const courtsSectionTitleId = `guest-club-${club.id}-courts-title`

  const goToBooking = (courtId?: number) => {
    const params = new URLSearchParams({ clubId: String(club.id) })
    if (courtId != null) {
      params.set('courtId', String(courtId))
    }
    navigate(`/guest-booking?${params.toString()}`)
  }

  const openMap = () => window.open(mapLink, '_blank', 'noopener,noreferrer')

  const perks: Perk[] = [
    ...(club.freeCancellation > 0
      ? [
          {
            key: 'cancellation',
            icon: 'check',
            title: t('clubs.guest.perks.cancellation.title', { count: club.freeCancellation }),
            text: t('clubs.guest.perks.cancellation.text'),
          },
        ]
      : []),
    ...(club.maxReschedules
      ? [
          {
            key: 'reschedule',
            icon: 'calendar',
            title: t('clubs.guest.perks.reschedule.title', { count: club.maxReschedules }),
            text: t('clubs.guest.perks.reschedule.text'),
          },
        ]
      : []),
    ...(club.startIncrementMinutes
      ? [
          {
            key: 'starts',
            icon: 'clock',
            title: t('clubs.guest.perks.starts.title', { minutes: club.startIncrementMinutes }),
            text: t('clubs.guest.perks.starts.text'),
          },
        ]
      : []),
    ...(litCount > 0
      ? [
          {
            key: 'lighting',
            icon: 'moon',
            title: t('clubs.guest.perks.lighting.title', { count: litCount }),
            text: t('clubs.guest.perks.lighting.text'),
          },
        ]
      : []),
  ]

  return (
    <div className="guest-club">
      {/* ── Hero ── */}
      <section className="guest-club-hero" aria-labelledby={`guest-club-${club.id}-name`}>
        <div className="guest-club-hero-glow" aria-hidden="true" />
        <div className="guest-club-hero-main">
          <div className="guest-club-hero-topline">
            <span className="guest-club-hero-eyebrow">
              {t('clubs.guest.hero.eyebrow', { city: club.address?.city || '' })}
            </span>
            {isOpen !== undefined && (
              <span className={`guest-club-status ${isOpen ? 'is-open' : 'is-closed'}`}>
                <span className="guest-club-status-dot" />
                {isOpen
                  ? t('clubs.guest.hero.open-until', { time: closeTime })
                  : t('clubs.guest.hero.closed-opens', { time: openTime })}
              </span>
            )}
          </div>

          <h1 id={`guest-club-${club.id}-name`} className="guest-club-hero-name">
            {club.name}
          </h1>

          {formattedAddress && (
            <p className="guest-club-hero-address">
              <Icon name="pin" size={16} />
              <span>{formattedAddress}</span>
            </p>
          )}

          <div className="guest-club-hero-actions">
            <Button
              className="guest-club-cta"
              label={t('clubs.guest.book')}
              icon={<Icon name="arrowR" size={16} />}
              iconPosition="right"
              size="lg"
              onClick={() => goToBooking()}
            />
            {formattedAddress && (
              <Button
                className="guest-club-hero-ghost"
                variant="ghost"
                label={t('clubs.guest.hero.directions')}
                icon={<Icon name="pin" size={15} />}
                size="lg"
                onClick={openMap}
              />
            )}
          </div>
        </div>

        <div className="guest-club-hero-art d-none d-lg-flex" aria-hidden="true">
          <div className="guest-club-hero-court guest-club-hero-court--back">
            <CourtSvg indoor={false} w={150} h={250} />
          </div>
          <div className="guest-club-hero-court guest-club-hero-court--front">
            <CourtSvg indoor w={170} h={280} />
          </div>
        </div>

        <dl className="guest-club-hero-stats">
          <div className="guest-club-stat">
            <dt>{t('clubs.guest.courts')}</dt>
            <dd>
              {courtsCount}
              {courts.length > 0 && (indoorCount > 0 || outdoorCount > 0) && (
                <small>
                  {[
                    indoorCount > 0 && t('clubs.guest.hero.indoor', { count: indoorCount }),
                    outdoorCount > 0 && t('clubs.guest.hero.outdoor', { count: outdoorCount }),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </small>
              )}
            </dd>
          </div>
          <div className="guest-club-stat">
            <dt>{t('clubs.guest.hours')}</dt>
            <dd>
              {openTime}
              <span className="guest-club-stat-sep">–</span>
              {closeTime}
            </dd>
          </div>
          {durations.length > 0 && (
            <div className="guest-club-stat">
              <dt>{t('clubs.guest.hero.sessions')}</dt>
              <dd>{durations.map(formatMinutes).join(' · ')}</dd>
            </div>
          )}
          <div className="guest-club-stat">
            <dt>{t('clubs.guest.cancellation')}</dt>
            <dd>{t('clubs.card.hours', { count: club.freeCancellation })}</dd>
          </div>
        </dl>
      </section>

      {/* ── Perks ── */}
      {perks.length > 0 && (
        <section className="guest-club-perks" aria-label={t('clubs.guest.perks.label')}>
          {perks.map((perk, index) => (
            <div key={perk.key} className="guest-club-perk" style={{ animationDelay: `${120 + index * 70}ms` }}>
              <span className="guest-club-perk-icon">
                <Icon name={perk.icon} size={18} />
              </span>
              <div>
                <div className="guest-club-perk-title">{perk.title}</div>
                <div className="guest-club-perk-text">{perk.text}</div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* ── Courts ── */}
      <section className="guest-club-courts" aria-labelledby={courtsSectionTitleId}>
        <div className="guest-club-section-heading">
          <div>
            <div className="eyebrow">{t('clubs.guest.courts-eyebrow')}</div>
            <h2 id={courtsSectionTitleId}>{t('clubs.guest.courts-title')}</h2>
          </div>
          {courts.length > 0 && (
            <span className="guest-club-count mono">{String(courts.length).padStart(2, '0')}</span>
          )}
        </div>

        {courtsLoading ? (
          <Loading variant="card" />
        ) : courts.length > 0 ? (
          <div className="guest-court-index row g-3">
            {courts.map(court => (
              <div
                key={court.id ?? court.name}
                className={`guest-court-column ${courts.length === 1 ? 'col-12' : 'col-12 col-lg-6'}`}
              >
                <GuestCourtCard court={court} onBook={() => goToBooking(court.id)} />
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">{t('clubs.guest.no-courts')}</p>
        )}
      </section>

      {/* ── Location ── */}
      {formattedAddress && (
        <section className="guest-club-location" aria-label={t('clubs.guest.map.eyebrow')}>
          <div className="guest-club-location-copy">
            <div>
              <div className="eyebrow">{t('clubs.guest.map.eyebrow')}</div>
              <p className="guest-club-location-address">{formattedAddress}</p>
            </div>
            <Button
              variant="ghost"
              label={t('clubs.guest.map.open')}
              icon={<Icon name="arrowR" size={14} />}
              iconPosition="right"
              onClick={openMap}
            />
          </div>
          <div className="guest-club-map">
            <iframe
              title={t('clubs.guest.map.title', { name: club.name })}
              src={mapEmbedSrc}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </section>
      )}

      {/* ── Final call to action ── */}
      <section className="guest-club-final">
        <div>
          <h2>{t('clubs.guest.final.title', { name: club.name })}</h2>
          <p>{t('clubs.guest.final.text')}</p>
        </div>
        <Button
          className="guest-club-final-cta"
          variant="dark"
          label={t('clubs.guest.book')}
          icon={<Icon name="arrowR" size={16} />}
          iconPosition="right"
          size="lg"
          onClick={() => goToBooking()}
        />
      </section>
    </div>
  )
}

export default GuestClubSection
